//@ts-strict-ignore
import React, { Component } from 'react';
import AttributeField from './AttributeField';
import Label from './Label';
import ICommonFieldProps from '../../@types/ICommonFieldProps';
import { DocumentNode } from '@apollo/client';
import { uuIdGenerator } from '../../utils/UuIdGenerator';

export type IAttributeArrayProps = ICommonFieldProps & {
  active?: boolean;
  clearButton?: boolean;
  mandatory?: boolean;
  includeLabel?: boolean;
  inputPrefix?: string;
  flexBox?: boolean;
  id?: string;
  hint?: string;
  value?: Array<any>;
  validationHint?: string;
  validationErrors?: any;
  showWhenNotActive?: boolean;
  label?: string;
  type:
    | 'customer'
    | 'supplier'
    | 'sales'
    | 'diary'
    | 'purchase'
    | 'product'
    | 'assembly'
    | 'customerContact'
    | 'supplierContact'
    | 'lot'
    | 'productCategory';
  onBlur?: Function;
  onChange?: Function;
  dataid?: string;
  includeCheckbox?: boolean;
  data?: any;
  query?: DocumentNode;
  initialized?: boolean;
  ignoreTypeForValue?: boolean;
  shouldDisplayAttributeReadOnly?: (attribute: any) => boolean;
};

export default class AttributeArray extends Component<IAttributeArrayProps, { errorInfo: any; error: any }> {
  static defaultProps: {
    active: boolean;
    showWhenNotActive: boolean;
    inputCols: number;
    label: string;
    mandatory: boolean;
    clearButton: boolean;
    hint: string;
    labelCols: number;
    includeLabel: boolean;
    validationErrors: any[];
    placeholder: string;
    id: string;
    value: any[];
    inputPrefix: null;
    flexBox: boolean;
  };

  constructor(props) {
    super(props);

    this.renderAttribute = this.renderAttribute.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
  }

  onBlur(updatedAttribute) {
    let attributes = this.props.value ? [...this.props.value] : [];
    const attributeIndex = attributes.findIndex((attribute) => {
      if (attribute.attributeConfig === null) return false;
      return attribute.attributeConfig.id === updatedAttribute.attributeConfig.id;
    });
    if (!updatedAttribute.uuid) {
      updatedAttribute.uuid = uuIdGenerator();
    }
    if (attributeIndex >= 0) {
      attributes = [...attributes];
      attributes[attributeIndex] = updatedAttribute;
    } else {
      attributes.push(updatedAttribute);
    }

    this.props.onBlur?.(attributes);
  }

  onChange(updatedAttribute) {
    let attributes = this.props.value ? [...this.props.value] : [];
    const attributeIndex = attributes.findIndex((attribute) => {
      if (attribute.attributeConfig === null) return false;
      return attribute.attributeConfig.id === updatedAttribute.attributeConfig.id;
    });
    if (updatedAttribute.amendValue === false) {
      updatedAttribute.referenceValue = null;
    }
    if (!updatedAttribute.uuid) {
      updatedAttribute.uuid = uuIdGenerator();
    }
    if (attributeIndex >= 0) {
      attributes = [...attributes];
      attributes[attributeIndex] = updatedAttribute;
    } else {
      attributes.push(updatedAttribute);
    }

    this.props.onChange?.(attributes);
  }

  /**
   * Renders an attribute, this is what we pass into GenericArray.tsx' 'renderRow' prop.
   * @param attribute - The attribute to be rendered. Must contain valid attributeConfig information.
   * @returns {*}
   */
  renderAttribute = (attribute) => {
    if (!attribute) return null;
    let readOnly: boolean = this.props?.readOnly ?? false;
    if (this.props.shouldDisplayAttributeReadOnly && !this.props.shouldDisplayAttributeReadOnly(attribute)) {
      readOnly = true;
    }
    const key = `_${this.props.dataid}${attribute.attributeConfig.id}`;

    return (
      <AttributeField
        {...this.props}
        value={attribute}
        onChange={this.onChange}
        onBlur={this.onBlur}
        key={key}
        id={key}
        dataid={key}
        testId={key}
        includeCheckbox={this.props.includeCheckbox}
        readOnly={readOnly}
      />
    );
  };

  render() {
    const { data: attributeConfigs, value = [] } = this.props;
    if (!attributeConfigs || !attributeConfigs.length) return null;

    const validationWarnings = this.props.validationHint ? (
      <Label bold={true}>{this.props.validationHint}</Label>
    ) : null;

    const renderedAttributes = attributeConfigs
      .map((attributeConfig) => {
        const valIndex = value
          ? value.findIndex((val) => val && val.attributeConfig && val.attributeConfig.id === attributeConfig.id)
          : -1;
        if (valIndex >= 0) {
          return { ...value[valIndex], attributeConfig: attributeConfig }; //Maintain dataid reference for existing attribute.
        }
        return { attributeConfig: attributeConfig };
      })
      .map((value) => {
        return this.renderAttribute(value);
      });

    return (
      <>
        {validationWarnings}
        {renderedAttributes}
      </>
    );
  }
}

AttributeArray.defaultProps = {
  active: false,
  mandatory: false,
  clearButton: false,
  placeholder: '',
  includeLabel: true,
  inputPrefix: null,
  flexBox: true,
  labelCols: 1 / 4,
  inputCols: 3 / 4,
  value: [],
  validationErrors: [],
  hint: '',
  showWhenNotActive: false,
  label: '',
  id: '',
};
